import React from 'react';
import { useTranslation } from 'react-i18next';

const ScreenHealthMonitor = ({ screens }) => {
  const { t } = useTranslation();
  
  console.log('==========================================');
  console.log('🔍 ScreenHealthMonitor FULL DEBUG');
  console.log('==========================================');
  
  // Safety check
  if (!Array.isArray(screens)) {
    console.warn('❌ screens is not an array', screens);
    return null;
  }

  const now = new Date();
  console.log('🕐 Current time:', now.toISOString());
  console.log('📊 Total screens received:', screens.length);
  
  // Detaillierte Analyse jedes Screens
  screens.forEach((s, idx) => {
    console.log(`\n--- Screen ${idx + 1}: ${s.name} ---`);
    console.log('  status:', s.status);
    console.log('  last_seen:', s.last_seen);
    console.log('  lastSeen:', s.lastSeen);
    console.log('  created_at:', s.created_at);
    
    const lastSeenValue = s.last_seen || s.lastSeen;
    if (lastSeenValue) {
      const lastSeenDate = new Date(lastSeenValue);
      const diffMs = now - lastSeenDate;
      const diffSeconds = diffMs / 1000;
      const diffMinutes = diffSeconds / 60;
      console.log('  ⏰ Age:', {
        seconds: diffSeconds.toFixed(1),
        minutes: diffMinutes.toFixed(1),
        hours: (diffMinutes / 60).toFixed(1)
      });
    } else {
      console.log('  ⚠️ NO last_seen at all!');
    }
  });
  
  const totalScreens = screens.length;
  
  // Online
  const onlineScreens = screens.filter(s => s.status === 'online').length;
  console.log('\n🟢 Online screens:', onlineScreens);
  
  // Offline
  const offlineScreens = screens.filter(s => s.status === 'offline');
  console.log('🔴 Offline screens:', offlineScreens.length, '→', offlineScreens.map(s => s.name));
  
  // Recently Offline
  const recentlyOffline = offlineScreens.filter(s => {
    const lastSeenValue = s.last_seen || s.lastSeen;
    
    if (!lastSeenValue) {
      console.log(`  ⚠️ ${s.name}: NO last_seen → classified as NOT recently`);
      return false;
    }
    
    const lastSeen = new Date(lastSeenValue);
    const diffMinutes = (now - lastSeen) / 1000 / 60;
    const isRecent = diffMinutes < 60;
    
    console.log(`  ${isRecent ? '🟡' : '🔴'} ${s.name}: ${diffMinutes.toFixed(1)} min ago → ${isRecent ? 'RECENTLY' : 'LONG'}`);
    
    return isRecent;
  });
  
  console.log('🟡 Recently Offline count:', recentlyOffline.length);

  // Long Offline
  const longOffline = offlineScreens.filter(s => {
    const lastSeenValue = s.last_seen || s.lastSeen;
    
    if (!lastSeenValue) {
      console.log(`  🔴 ${s.name}: NO last_seen → classified as LONG`);
      return true;
    }
    
    const lastSeen = new Date(lastSeenValue);
    const diffMinutes = (now - lastSeen) / 1000 / 60;
    const isLong = diffMinutes >= 60;
    
    console.log(`  ${isLong ? '🔴' : '🟡'} ${s.name}: ${diffMinutes.toFixed(1)} min ago → ${isLong ? 'LONG' : 'RECENT'}`);
    
    return isLong;
  });

  console.log('🔴 Long Offline count:', longOffline.length);
  console.log('==========================================\n');

  return (
  <div className="grid grid-cols-2 gap-3 mb-4">
    {/* Total */}
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700 min-h-[88px]">
      <div className="flex items-start justify-between h-full">
        <div className="flex-1 flex flex-col">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-2">
            {t('dashboard.totalScreens')}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white leading-none mt-auto">
            {totalScreens}
          </p>
        </div>
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
          <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
      </div>
    </div>

    {/* Online */}
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700 min-h-[88px]">
      <div className="flex items-start justify-between h-full">
        <div className="flex-1 flex flex-col">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
            {t('dashboard.online')}
          </p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 leading-none mt-auto">
            {onlineScreens}
          </p>
        </div>
        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
          <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
    </div>

    {/* Recently Offline */}
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700 min-h-[88px]">
      <div className="flex items-start justify-between h-full">
        <div className="flex-1 flex flex-col">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
            {t('dashboard.recentlyOffline')}
          </p>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 leading-none">
            {recentlyOffline.length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-auto">
            {t('dashboard.lessThanHour')}
          </p>
        </div>
        <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
          <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
    </div>

    {/* Long Offline */}
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700 min-h-[88px]">
      <div className="flex items-start justify-between h-full">
        <div className="flex-1 flex flex-col">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
            {t('dashboard.longOffline')}
          </p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400 leading-none">
            {longOffline.length}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-auto">
            {t('dashboard.moreThanHour')}
          </p>
        </div>
        <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0 ml-2">
          <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
      </div>
    </div>
  </div>
);
};

export default ScreenHealthMonitor;